import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime } from 'rxjs';

import { EmptyStateComponent } from '../../../components/ui/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../components/ui/error-state/error-state.component';
import {
  AuditActor,
  AuditEntry,
  AuditSearchCriteria,
  ConnectionMetrics,
  UserActivitySummary,
  UserSession,
} from '../../../core/interfaces/activity';
import { PaginationInfo } from '../../../core/interfaces/question-search';
import { ActivatedRoute } from '@angular/router';
import { AdminActivityService } from '../../../core/services/admin/admin-activity.service';

/** Les deux lectures de la même matière : les actes, et la fréquentation. */
type ActivityTab = 'journal' | 'connections';

/** Rôles proposés en facette. `public` couvre les actes anonymes du site. */
const ROLES = ['supervisor', 'professor', 'student', 'public'] as const;

/** Fenêtres proposées, en jours. `0` vaut « depuis toujours ». */
const WINDOWS = [1, 7, 30, 90, 0] as const;

const PAGE_SIZE = 50;

/**
 * Lignes par page du tableau « activité par compte ».
 *
 * <p>Plus court que le journal : ce tableau se lit de haut en bas pour trouver
 * qui ne vient plus, et un établissement compte vite quelques centaines de
 * comptes. Il est alimenté d'un bloc par le serveur, la découpe est donc
 * locale.</p>
 */
const USER_PAGE_SIZE = 20;

/**
 * Journal d'activité et fréquentation.
 *
 * <p>Deux onglets pour deux questions qui se répondent : « qui a fait quoi »
 * et « qui se connecte ». Les séparer en deux écrans obligerait à faire des
 * allers-retours pour rapprocher un acte suspect d'une session ; les réunir
 * dans une seule liste mélangerait des lignes qui ne se comparent pas.</p>
 *
 * <p>Le composant ne fabrique jamais de données. Une requête en échec produit
 * un état d'erreur explicite ; une page légitimement vide produit un état
 * vide.</p>
 */
@Component({
  selector: 'app-admin-activity',
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    TranslateModule,
    EmptyStateComponent,
    ErrorStateComponent,
  ],
  templateUrl: './admin-activity.component.html',
  styleUrl: './admin-activity.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminActivityComponent implements OnInit {
  private readonly service = inject(AdminActivityService);

  /**
   * Périmètre du journal, déclaré par la route.
   *
   * <p>`scope: 'own'` restreint l'écran aux actions de l'appelant, et bascule
   * sur l'endpoint où le serveur impose l'auteur. C'est la route qui le dit,
   * jamais l'utilisateur : un périmètre que l'écran laisserait choisir n'en
   * serait pas un.</p>
   */
  protected readonly ownScope =
    inject(ActivatedRoute).snapshot.data['scope'] === 'own';

  readonly roles = ROLES;
  readonly windows = WINDOWS;
  readonly skeletonRows = Array.from({ length: 8 }, (_, i) => i);

  readonly tab = signal<ActivityTab>('journal');

  /* ── Facettes communes ────────────────────────────────────── */
  readonly windowDays = signal<number>(7);
  readonly role = signal<string | null>(null);
  readonly text = signal('');
  /** Texte tapé, avant débounce — sans quoi le curseur sauterait à chaque frappe. */
  readonly textDraft = signal('');

  /* ── Facettes du journal ──────────────────────────────────── */
  readonly actorUserId = signal<string | null>(null);
  readonly action = signal<string | null>(null);
  readonly failuresOnly = signal(false);

  /* ── Journal ──────────────────────────────────────────────── */
  readonly entries = signal<AuditEntry[]>([]);
  readonly entriesPagination = signal<PaginationInfo | null>(null);
  readonly entriesPage = signal(1);
  readonly actors = signal<AuditActor[]>([]);
  readonly actions = signal<string[]>([]);

  /* ── Fréquentation ────────────────────────────────────────── */
  readonly sessions = signal<UserSession[]>([]);
  readonly sessionsPagination = signal<PaginationInfo | null>(null);
  readonly sessionsPage = signal(1);
  readonly metrics = signal<ConnectionMetrics | null>(null);
  readonly userActivity = signal<UserActivitySummary[]>([]);
  readonly userPage = signal(1);

  /* ── État ─────────────────────────────────────────────────── */
  readonly loading = signal(false);
  readonly errored = signal(false);
  readonly errorDetail = signal('');

  private readonly textInput$ = new Subject<string>();

  /* ── Dérivé ───────────────────────────────────────────────── */

  readonly currentPagination = computed(() =>
    this.tab() === 'journal' ? this.entriesPagination() : this.sessionsPagination(),
  );
  readonly currentPage = computed(
    () => this.currentPagination()?.currentPage ?? 1,
  );
  readonly totalPages = computed(() => this.currentPagination()?.totalPages ?? 0);
  readonly total = computed(() => this.currentPagination()?.total ?? 0);

  readonly canPrevious = computed(() => !this.loading() && this.currentPage() > 1);
  readonly canNext = computed(
    () => !this.loading() && this.currentPage() < this.totalPages(),
  );

  readonly isEmpty = computed(() => {
    if (this.loading() || this.errored()) return false;
    return this.tab() === 'journal'
      ? this.entries().length === 0
      : this.sessions().length === 0;
  });

  readonly activeCount = computed(() => {
    let n = 0;
    if (this.role()) n++;
    if (this.text().trim()) n++;
    if (this.windowDays() !== 7) n++;
    if (this.tab() === 'journal') {
      if (this.actorUserId()) n++;
      if (this.action()) n++;
      if (this.failuresOnly()) n++;
    }
    return n;
  });

  readonly hasActiveFilters = computed(() => this.activeCount() > 0);

  readonly userTotalPages = computed(() =>
    Math.max(1, Math.ceil(this.userActivity().length / USER_PAGE_SIZE)),
  );

  /** Page courante, bornée : changer de rôle peut réduire le nombre de pages. */
  readonly userCurrentPage = computed(() =>
    Math.min(this.userPage(), this.userTotalPages()),
  );

  readonly pagedUserActivity = computed(() => {
    const start = (this.userCurrentPage() - 1) * USER_PAGE_SIZE;
    return this.userActivity().slice(start, start + USER_PAGE_SIZE);
  });

  /** Le plus haut total journalier, pour dimensionner les barres de la courbe. */
  readonly peakDay = computed(() =>
    Math.max(1, ...(this.metrics()?.byDay ?? []).map((d) => d.sessionCount)),
  );

  constructor() {
    // Sans débounce, chaque frappe partirait au serveur : une recherche de huit
    // caractères déclencherait huit requêtes concurrentes sur une table qui a
    // vocation à devenir la plus volumineuse de la base.
    this.textInput$
      .pipe(debounceTime(350), takeUntilDestroyed())
      .subscribe((value) => {
        // Une frappe encore en attente au moment de la remise à zéro est
        // périmée : l'appliquer rétablirait une recherche que le champ, vidé,
        // ne montre plus.
        if (value !== this.textDraft()) return;
        this.text.set(value);
        this.resetPagesAndReload();
      });
  }

  ngOnInit(): void {
    this.loadFacets();
    this.reload();
  }

  /* ── Navigation ───────────────────────────────────────────── */

  selectTab(tab: ActivityTab): void {
    if (this.tab() === tab) return;
    // La fréquentation lit les connexions de tous : elle exige
    // `activity.read.all`, que « mon journal » n'a pas.
    if (tab === 'connections' && this.ownScope) return;
    this.tab.set(tab);
    // Les facettes propres au journal n'ont pas de sens sur la fréquentation :
    // les laisser actives ferait afficher un onglet muet sans qu'on voie pourquoi.
    if (tab === 'connections') {
      this.actorUserId.set(null);
      this.action.set(null);
      this.failuresOnly.set(false);
    }
    this.reload();
  }

  /* ── Facettes ─────────────────────────────────────────────── */

  onWindow(raw: string): void {
    this.windowDays.set(Number(raw));
    this.resetPagesAndReload();
  }

  onRole(raw: string): void {
    this.role.set(raw || null);
    this.resetPagesAndReload();
  }

  onActor(raw: string): void {
    this.actorUserId.set(raw || null);
    this.resetPagesAndReload();
  }

  onAction(raw: string): void {
    this.action.set(raw || null);
    this.resetPagesAndReload();
  }

  onFailuresOnly(checked: boolean): void {
    this.failuresOnly.set(checked);
    this.resetPagesAndReload();
  }

  onText(raw: string): void {
    this.textDraft.set(raw);
    this.textInput$.next(raw);
  }

  resetFilters(): void {
    this.windowDays.set(7);
    this.role.set(null);
    this.actorUserId.set(null);
    this.action.set(null);
    this.failuresOnly.set(false);
    this.text.set('');
    this.textDraft.set('');
    this.resetPagesAndReload();
  }

  /* ── Pagination ───────────────────────────────────────────── */

  previousPage(): void {
    if (!this.canPrevious()) return;
    this.goToPage(this.currentPage() - 1);
  }

  nextPage(): void {
    if (!this.canNext()) return;
    this.goToPage(this.currentPage() + 1);
  }

  previousUserPage(): void {
    if (this.userCurrentPage() > 1) this.userPage.set(this.userCurrentPage() - 1);
  }

  nextUserPage(): void {
    if (this.userCurrentPage() < this.userTotalPages())
      this.userPage.set(this.userCurrentPage() + 1);
  }

  retry(): void {
    this.reload();
  }

  /* ── Libellés ─────────────────────────────────────────────── */

  roleKey(role?: string | null): string {
    return `ACTIVITY.ROLE.${(role ?? 'unknown').toUpperCase()}`;
  }

  /**
   * Clé i18n d'un acte nommé.
   *
   * <p>Les actes non traduits — les replis `http.put`, ou un acte nommé après
   * la dernière passe de traduction — sont rendus tels quels par
   * {@link actionLabel} plutôt qu'affichés comme une clé manquante.</p>
   */
  actionKey(action: string): string {
    return `ACTIVITY.ACTION.${action.replace(/[.\-]/g, '_').toUpperCase()}`;
  }

  /**
   * Cible lisible.
   *
   * <p>Le libellé figé d'abord, puis ce que la route a permis de deviner. Les
   * deux parties étaient auparavant exigées ensemble : un acte non décrit
   * n'ayant qu'un identifiant, la colonne restait vide sur la quasi-totalité du
   * journal. Chaque morceau vaut mieux que rien — « course-materials #2 » se
   * lit, « #2 » aussi.</p>
   */
  targetLabel(entry: AuditEntry): string {
    if (entry.label?.trim()) return entry.label;

    const type = entry.entityType?.trim();
    const id = entry.entityId?.trim();

    if (type && id) return `${type} #${id}`;
    if (type) return type;
    if (id) return `#${id}`;
    return '—';
  }

  /* ── Interne ──────────────────────────────────────────────── */

  private goToPage(page: number): void {
    if (this.tab() === 'journal') this.entriesPage.set(page);
    else this.sessionsPage.set(page);
    this.reload();
  }

  private resetPagesAndReload(): void {
    // Changer une facette invalide la position dans la pagination : rester
    // page 12 sur un résultat qui n'en compte plus que 2 renverrait du vide.
    this.entriesPage.set(1);
    this.sessionsPage.set(1);
    this.reload();
  }

  /** Borne basse de la période, ou `undefined` pour « depuis toujours ». */
  private from(): string | undefined {
    const days = this.windowDays();
    if (!days) return undefined;
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString();
  }

  /**
   * Requêtes en vol. Chaque rechargement abandonne les précédentes : deux
   * facettes changées coup sur coup lançaient deux recherches, et la plus
   * lente — la moins filtrée, donc souvent la première — écrasait le résultat
   * que l'écran annonçait.
   */
  private listRequest?: Subscription;
  private metricsRequest?: Subscription;
  private userActivityRequest?: Subscription;

  private reload(): void {
    this.listRequest?.unsubscribe();
    this.metricsRequest?.unsubscribe();
    this.userActivityRequest?.unsubscribe();
    this.loading.set(true);
    this.errored.set(false);
    this.errorDetail.set('');

    if (this.tab() === 'journal') this.loadJournal();
    else this.loadConnections();
  }

  private loadJournal(): void {
    const criteria: AuditSearchCriteria = {
      actorUserId: this.actorUserId() ?? undefined,
      actorRole: this.role() ?? undefined,
      action: this.action() ?? undefined,
      from: this.from(),
      failuresOnly: this.failuresOnly() || undefined,
      text: this.text().trim() || undefined,
      page: this.entriesPage(),
      pageSize: PAGE_SIZE,
    };

    // Deux endpoints, un seul écran. Le périmètre vient de la route et non
    // d'un choix de l'utilisateur : « mon journal » et « le journal de tous »
    // sont deux droits distincts, et c'est le serveur qui impose l'auteur dans
    // le premier cas.
    const request = this.ownScope
      ? this.service.searchMyAudit(criteria)
      : this.service.searchAudit(criteria);

    this.listRequest = request.subscribe({
      next: (response) => {
        if (response?.status !== 'SUCCESS') {
          // Une enveloppe en échec n'est pas un journal vide : les distinguer
          // évite d'annoncer « aucune activité » alors que rien n'a été lu.
          this.fail(response?.message ?? '');
          return;
        }
        this.entries.set(response.data ?? []);
        this.entriesPagination.set(response.pagination ?? null);
        this.loading.set(false);
      },
      error: (error) => this.fail(detailOf(error)),
    });
  }

  private loadConnections(): void {
    const from = this.from();

    this.listRequest = this.service
      .searchSessions({
        role: this.role() ?? undefined,
        from,
        text: this.text().trim() || undefined,
        page: this.sessionsPage(),
        pageSize: PAGE_SIZE,
      })
      .subscribe({
        next: (response) => {
          if (response?.status !== 'SUCCESS') {
            this.fail(response?.message ?? '');
            return;
          }
          this.sessions.set(response.data ?? []);
          this.sessionsPagination.set(response.pagination ?? null);
          this.loading.set(false);
        },
        error: (error) => this.fail(detailOf(error)),
      });

    // Les deux compléments sont accessoires : leur échec laisse le tableau
    // utilisable plutôt que de faire basculer tout l'écran en erreur.
    this.metricsRequest = this.service.metrics(from).subscribe({
      next: (response) =>
        this.metrics.set(response?.status === 'SUCCESS' ? (response.data ?? null) : null),
      error: () => this.metrics.set(null),
    });

    this.userActivityRequest = this.service.userActivity(this.role() ?? undefined).subscribe({
      next: (response) => {
        this.userActivity.set(response?.status === 'SUCCESS' ? (response.data ?? []) : []);
        // Le contenu change : rester page 7 d'une liste qui n'en compte plus
        // que 2 afficherait du vide.
        this.userPage.set(1);
      },
      error: () => {
        this.userActivity.set([]);
        this.userPage.set(1);
      },
    });
  }

  /**
   * Facettes du journal, chargées une fois.
   *
   * <p>Auteurs et actes viennent des données et non d'une liste écrite à la
   * main : le vocabulaire s'enrichit à mesure que les actes sont nommés, et
   * une liste figée divergerait dès le premier ajout. Leur échec ne fait pas
   * basculer l'écran — la recherche libre reste utilisable.</p>
   */
  private loadFacets(): void {
    // La facette « auteur » n'a pas d'objet quand il n'y en a qu'un — et son
    // endpoint exige `activity.read.all`, que l'appelant n'a peut-être pas :
    // la demander lui vaudrait un 403 à chaque ouverture.
    if (this.ownScope) {
      this.actors.set([]);
    } else {
      this.service.auditActors().subscribe({
        next: (response) =>
          this.actors.set(response?.status === 'SUCCESS' ? (response.data ?? []) : []),
        error: () => this.actors.set([]),
      });
    }

    // La facette « acte », elle, a un sens dans les deux périmètres — mais
    // chacun la demande à l'endpoint que son droit ouvre : la liste de tous
    // les actes exige `activity.read.all`.
    const actions$ = this.ownScope ? this.service.myAuditActions() : this.service.auditActions();
    actions$.subscribe({
      next: (response) =>
        this.actions.set(response?.status === 'SUCCESS' ? (response.data ?? []) : []),
      error: () => this.actions.set([]),
    });
  }

  private fail(detail: string): void {
    this.entries.set([]);
    this.sessions.set([]);
    this.entriesPagination.set(null);
    this.sessionsPagination.set(null);
    this.errorDetail.set(detail);
    this.errored.set(true);
    this.loading.set(false);
  }
}

function detailOf(error: { status?: number; message?: string }): string {
  return error?.status ? `HTTP ${error.status}` : (error?.message ?? '');
}
